import i18next from 'i18next';
import React from 'react';

// Commons
import { CONSUMPTION_FREQUENCY } from '@admin/commons/constants/nonSalesConsumption';
import { ConsumptionFrequency } from '@admin/stores/StoreDetailsModules/StoreBufferStocks/styledComponents';
import { Tags } from '@commons/Tags';
import { translateUnit } from '@commons/utils/translateUnit';

export const getColumns = (hasCentralKitchens, hasGroups, currency) => {
  const columns = [
    {
      id: 'name',
      name: i18next.t('GENERAL.NAME'),
      propertyKey: 'name',
      // The render method is necessary otherwise the text will overflow if the name is too long
      render: (item) => item,
    },
    {
      id: 'bufferStock',
      name: i18next.t('ADMIN.STORES.BUFFER_STOCK_COLUMN_TITLE'),
      tooltipText: i18next.t('ADMIN.INGREDIENTS.BUFFER_STOCK_COLUMN_TOOLTIP'),
      propertyKey: 'bufferStock',
      excludeFromSearch: true,

      render: (days) => i18next.t('ADMIN.STORES.BUFFER_STOCK_COLUMN_VALUE', { days }),
    },
    {
      id: 'consumptionQuantity',
      name: i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE'),
      tooltipText: i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE_TOOLTIP'),
      propertyKey: 'consumptionQuantity',
      filterType: 'numeric',
      excludeFromSearch: true,
      render: (consumptionQuantity, { ingredientUnit }) =>
        `${consumptionQuantity || '-'} ${translateUnit(ingredientUnit)}`,
    },
    {
      id: 'consumptionFrequency',
      name: i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE'),
      tooltipText: i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE_TOOLTIP', {
        currency,
      }),
      propertyKey: 'consumptionFrequency',
      filterType: 'string',
      excludeFromSearch: true,
      render: (consumptionFrequency) => {
        if (!consumptionFrequency) {
          return '';
        }

        const isDailyConsumption = consumptionFrequency === CONSUMPTION_FREQUENCY.DAILY;

        return (
          <ConsumptionFrequency isDailyConsumption={isDailyConsumption}>
            {isDailyConsumption
              ? i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_DAILY')
              : i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_PER_1000_CURRENCY', {
                  currency,
                })}
          </ConsumptionFrequency>
        );
      },
    },
  ];

  if (hasCentralKitchens) {
    columns.splice(1, 0, {
      id: 'isKitchen',
      name: i18next.t('GENERAL.STORE_TYPE'),
      propertyKey: 'isKitchen',
      isHidden: !hasCentralKitchens,
      render: (isKitchen) =>
        isKitchen ? i18next.t('GENERAL.CENTRAL') : i18next.t('GENERAL.SALE_POINT'),
    });
  }

  if (hasGroups) {
    columns.splice(hasCentralKitchens ? 2 : 1, 0, {
      id: 'groups',
      propertyKey: 'groups',
      name: i18next.t('GENERAL.GROUPS'),
      disableSort: true,
      render: (groups) => {
        if (!groups) {
          return <Tags items={[]} />;
        }

        return <Tags items={groups.map(({ name }) => name)} />;
      },
      transform: (groups) => groups.map(({ name }) => name).join(', '),
    });
  }

  return columns;
};

export const getActions = ({
  openBufferStockModal,
  openNonSalesConsumptionModal,
  exportStoreConfigXLS,
  associatedStores,
  storeName,
  hasCentralKitchens,
  currency,
}) => [
  {
    label: i18next.t('ADMIN.STORES.MANAGE_BUFFER_STOCKS_ACTIONS'),
    handleClick: openBufferStockModal,
    icon: () => '/images/inpulse/gear-black-small.svg',
  },
  {
    label: i18next.t('ADMIN.STORES.MANAGE_NON_SALES_CONSUMPTION_ACTIONS'),
    handleClick: openNonSalesConsumptionModal,
    icon: () => '/images/inpulse/gear-black-small.svg',
  },
  {
    label: i18next.t('GENERAL.EXPORT_LIST'),
    handleClick: () =>
      exportStoreConfigXLS(associatedStores, storeName, hasCentralKitchens, currency),
    icon: () => '/images/inpulse/file-download-black-small.svg',
  },
];

// Do not delete, this fake data is necessary to show the upgrade plan view
export const FAKE_DATA_FOR_PLAN_UPGRADE = [
  {
    id: 1,
    name: 'Paris',
    isKitchen: false,
    groups: [{ name: 'IDF' }],
    bufferStock: 1,
  },
  {
    id: 2,
    name: 'Brest',
    isKitchen: false,
    groups: [{ name: 'Bretagne' }],
    bufferStock: 2,
  },
  {
    id: 3,
    name: 'Angers',
    isKitchen: false,
    groups: [{ name: 'Maine-et-Loire' }, { name: 'France' }],
    bufferStock: 3,
  },
  {
    id: 4,
    name: 'Marseille',
    isKitchen: false,
    groups: [{ name: 'Provence' }, { name: 'France' }],
    bufferStock: 4,
  },
  {
    id: 5,
    name: 'Lyon',
    isKitchen: true,
    groups: [{ name: 'Auvergne' }],
    bufferStock: 5,
  },
];
